import type {
  CustomerProfileField,
  PersonDataField,
} from '@/hooks/usePersonFieldRenderer';
import type { TFunction } from 'i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

export const createCheckoutPassengerSchema = (
  t: TFunction,
  passengerFields: Array<PersonDataField>
) => {
  const passengerFieldRequirementsMap = passengerFields.reduce<
    Partial<Record<PersonDataField['name'], PersonDataField['isRequired']>>
  >((acc, cur) => ({ ...acc, [cur.name]: cur.isRequired }), {});

  const isVisible = (name: PersonDataField['name']) =>
    passengerFields.some((field) => field.name === name);

  const email = !isVisible('email')
    ? z.union([z.literal(''), z.string()])
    : passengerFieldRequirementsMap['email']
      ? z
          .string()
          .min(1, { message: t('text.mustBeFilled') })
          .email({ message: t('text.invalidEmailFormat') })
      : z.union([
          z.literal(''),
          z.string().email({ message: t('text.invalidEmailFormat') }),
        ]);

  const phoneNumber = !isVisible('phoneNumber')
    ? z.union([z.literal(''), z.string()])
    : passengerFieldRequirementsMap['phoneNumber']
      ? z
          .string()
          .min(1, { message: t('text.mustBeFilled') })
          .refine((val) => !val || isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          })
      : z.union([
          z.literal(''),
          z.string().refine((val) => !val || isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          }),
        ]);

  return z.object({
    id: z.string(),
    externalReference: z.string(),
    firstName: passengerFieldRequirementsMap['firstName']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    lastName: passengerFieldRequirementsMap['lastName']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    email,
    phoneNumber,
    birthDate: passengerFieldRequirementsMap['birthDate']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    gender: passengerFieldRequirementsMap['gender']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    zipCode: passengerFieldRequirementsMap['zipCode']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]).optional(),
    stateCode: passengerFieldRequirementsMap['stateCode']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]).optional(),
    companyUnitCode: passengerFieldRequirementsMap['companyUnitCode']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]).optional(),
  });
};

export const createCheckoutPurchaserSchema = (
  t: TFunction,
  purchaserFields: Array<PersonDataField>
) => {
  const purchaserFieldRequirementsMap = purchaserFields.reduce<
    Partial<Record<PersonDataField['name'], PersonDataField['isRequired']>>
  >((acc, cur) => ({ ...acc, [cur.name]: cur.isRequired }), {});

  const isVisible = (name: PersonDataField['name']) =>
    purchaserFields.some((field) => field.name === name);

  const email = !isVisible('email')
    ? z.union([z.literal(''), z.string()])
    : purchaserFieldRequirementsMap['email']
      ? z
          .string()
          .min(1, { message: t('text.mustBeFilled') })
          .email({ message: t('text.invalidEmailFormat') })
      : z.union([
          z.literal(''),
          z.string().email({ message: t('text.invalidEmailFormat') }),
        ]);

  const phoneNumber = !isVisible('phoneNumber')
    ? z.union([z.literal(''), z.string()])
    : purchaserFieldRequirementsMap['phoneNumber']
      ? z
          .string()
          .min(1, { message: t('text.mustBeFilled') })
          .refine((val) => !val || isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          })
      : z.union([
          z.literal(''),
          z.string().refine((val) => !val || isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          }),
        ]);

  return z.object({
    externalReference: z.string().optional(),
    firstName: purchaserFieldRequirementsMap['firstName']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    lastName: purchaserFieldRequirementsMap['lastName']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]),
    email,
    phoneNumber,
    streetName: purchaserFieldRequirementsMap['streetName']
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.union([z.literal(''), z.string()]).optional(),
  });
};

export const createCheckoutSchema = (
  t: TFunction,
  passengerFields: Array<PersonDataField>,
  purchaserFields: Array<PersonDataField>
) => {
  return z
    .object({
      passengers: z.array(createCheckoutPassengerSchema(t, passengerFields)),
      purchaser: createCheckoutPurchaserSchema(t, purchaserFields),
      consents: z.array(
        z.object({
          isMandatory: z.boolean(),
          description: z.string().optional(),
          checked: z.boolean(),
          id: z.union([z.number(), z.string()]),
          code: z.union([z.string(), z.undefined()]),
        })
      ),
    })
    .superRefine(({ consents }, ctx) => {
      consents.forEach((consent, index) => {
        if (consent.isMandatory && !consent.checked) {
          ctx.addIssue({
            path: ['consents', index],
            code: z.ZodIssueCode.custom,
            message: '',
          });
        }
      });
    });
};

export type CheckoutValues = z.infer<ReturnType<typeof createCheckoutSchema>>;

export const createAncillarySchema = (t: TFunction) => {
  return z.object({
    selectedAncillaries: z
      .array(
        z.object({
          id: z.string(),
          bookedOfferId: z.string(),
          additionalOfferId: z.string(),
          amount: z.number(),
          passengersExternalReferences: z
            .array(z.string())
            .min(1, { message: t('text.minimumOne') }),
        })
      )
      .min(1, { message: t('text.minimumOne') }),
  });
};

export type AncillaryValues = z.infer<ReturnType<typeof createAncillarySchema>>;

export const createFindBookingSchema = (t: TFunction) => {
  return z.object({
    email: z
      .string()
      .min(1, { message: t('text.mustBeFilled') })
      .email({ message: t('text.invalidEmailFormat') }),
    bookingNumber: z.string().min(1, { message: t('text.mustBeFilled') }),
  });
};

export type FindBookingValues = z.infer<
  ReturnType<typeof createFindBookingSchema>
>;

export const createLoginSchema = (t: TFunction) => {
  return z.object({
    email: z
      .string()
      .min(1, { message: t('text.mustBeFilled') })
      .email({ message: t('text.invalidEmailFormat') }),
    password: z
      .string()
      .min(8, { message: t('text.minimumEight') })
      .max(64, { message: t('text.maximum64') }),
  });
};

export type LoginValues = z.infer<ReturnType<typeof createLoginSchema>>;

export const createSignupSchema = (
  t: TFunction,
  passwordRegexString: string | undefined,
  options?: { isTara?: boolean }
) => {
  const isTara = options?.isTara ?? false;

  return z
    .object({
      email: z
        .string()
        .min(1, { message: t('text.requiredField') })
        .email({ message: t('text.invalidEmailFormat') }),
      firstName: z.string().min(1, { message: t('text.requiredField') }),
      lastName: z.string().min(1, { message: t('text.requiredField') }),
      birthDate: z.string().min(1, { message: t('text.requiredField') }),
      // Gender / phone are out of scope for TARA (MaaS) registration — BR-56868.
      genderId: isTara
        ? z.string().optional()
        : z.string().min(1, { message: t('text.requiredField') }),
      phoneNumber: z.string().optional(),
      password: z.string().optional(),
      repeatPassword: z.string().optional(),
      consents: z.array(
        z.object({
          isMandatory: z.boolean(),
          description: z.string().optional(),
          checked: z.boolean(),
          id: z.union([z.number(), z.string()]),
          code: z.union([z.string(), z.undefined()]),
        })
      ),
    })
    .superRefine(({ password, repeatPassword, consents }, ctx) => {
      if (isTara) {
        return;
      }

      if (!new RegExp(passwordRegexString || '').test(password ?? '')) {
        ctx.addIssue({
          path: ['password'],
          code: z.ZodIssueCode.custom,
          message: t('text.doesntMatchCriteriaDescribedAbove'),
        });
      }

      if (password !== repeatPassword) {
        ctx.addIssue({
          path: ['password'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
        ctx.addIssue({
          path: ['repeatPassword'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
      }

      consents.forEach((consent, index) => {
        if (consent.isMandatory && !consent.checked) {
          ctx.addIssue({
            path: ['consents', index],
            code: z.ZodIssueCode.custom,
            message: '', // No message needed, red text for consent description is enough
          });
        }
      });
    });
};

export type SignupValues = z.infer<ReturnType<typeof createSignupSchema>>;

export const createPasswordResetLinkSchema = () => {
  return z.object({
    email: z.string().email(),
  });
};

export type PasswordResetLinkValues = z.infer<
  ReturnType<typeof createPasswordResetLinkSchema>
>;

export const createResetPasswordSchema = (
  t: TFunction,
  passwordRegexString: string | undefined
) => {
  return z
    .object({
      password: z.string().regex(new RegExp(passwordRegexString || ''), {
        message: t('text.doesntMatchCriteriaDescribedAbove'),
      }),
      repeatPassword: z.string(),
    })
    .superRefine(({ password, repeatPassword }, ctx) => {
      if (password !== repeatPassword) {
        ctx.addIssue({
          path: ['password'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
        ctx.addIssue({
          path: ['repeatPassword'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
      }
    });
};

export type ResetPasswordValues = z.infer<
  ReturnType<typeof createResetPasswordSchema>
>;

export const createPromoCodesSchema = (t: TFunction) => {
  return z.object({
    promoCode: z.string().min(1, { message: t('text.mustBeFilled') }),
  });
};

export type PromoCodesValues = z.infer<
  ReturnType<typeof createPromoCodesSchema>
>;

export const createChangePasswordSchema = (
  t: TFunction,
  passwordRegexString: string | undefined
) => {
  return z
    .object({
      currentPassword: z.string().min(1, { message: t('text.mustBeFilled') }),
      newPassword: z.string().regex(new RegExp(passwordRegexString || ''), {
        message: t('text.doesntMatchCriteriaDescribedAbove'),
      }),
      repeatNewPassword: z.string(),
    })
    .superRefine(({ newPassword, repeatNewPassword }, ctx) => {
      if (newPassword !== repeatNewPassword) {
        ctx.addIssue({
          path: ['newPassword'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
        ctx.addIssue({
          path: ['repeatNewPassword'],
          code: z.ZodIssueCode.custom,
          message: t('text.passwordsMustMatch'),
        });
      }
    });
};

export type ChangePasswordValues = z.infer<
  ReturnType<typeof createChangePasswordSchema>
>;

export const createVouchersSchema = (t: TFunction) => {
  return z.object({
    voucherCode: z.string().min(1, { message: t('text.mustBeFilled') }),
    _meta: z
      .object({
        customCode: z.string().optional(),
        // Selection is keyed by voucher CODE: the backend voucher `id` is
        // optional in the DTO and DEV omits it, and the code is what gets
        // submitted anyway (BR-51922). CUSTOM_VOUCHER_CODE_KEY marks the
        // free-text row.
        selectedVoucherCode: z.string().nullable().optional(),
      })
      .optional(),
  });
};

export type VouchersValues = z.infer<ReturnType<typeof createVouchersSchema>>;

export const createSendTicketsToEmailSchema = (t: TFunction) => {
  return z
    .object({
      bookingId: z.string(),
      passengers: z
        .array(
          z.object({
            passengerId: z.string(),
            sendSms: z.boolean(),
            sendEmail: z.boolean(),
            emailOverride: z.array(
              z.union([
                z.literal(''),
                z
                  .string()
                  .trim()
                  .email({ message: t('text.invalidEmailFormat') }),
              ])
            ),
            phoneNumberOverride: z.optional(z.array(z.string())),
          })
        )
        .optional(),
      purchaser: z.object({
        sendSms: z.boolean(),
        sendEmail: z.boolean(),
        emailOverride: z.array(
          z.union([
            z.literal(''),
            z
              .string()
              .trim()
              .email({ message: t('text.invalidEmailFormat') }),
          ])
        ),
        phoneNumberOverride: z.optional(z.array(z.string())),
      }),
      includeTaxInvoices: z.boolean().optional(),
      _meta: z.object({
        sendAllTickets: z.boolean(),
      }),
    })
    .superRefine((data, ctx) => {
      data.passengers?.forEach((p, i) => {
        if (!p.emailOverride?.[0]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('text.mustAddEmailToEveryRecipient'),
            path: ['passengers', i, 'emailOverride'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      data.purchaser.emailOverride.forEach((email, i) => {
        if (data._meta.sendAllTickets && email === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('text.missingEmail'),
            path: ['purchaser', 'emailOverride', i],
          });
        }
      });
    });
};

export const createUpdateProfileSchema = (
  t: TFunction,
  customerProfileFields: Array<CustomerProfileField>
) => {
  const requirements = customerProfileFields.reduce<
    Partial<Record<CustomerProfileField['name'], boolean>>
  >((acc, { name, isRequired }) => ({ ...acc, [name]: isRequired }), {});

  const isVisible = (name: CustomerProfileField['name']) =>
    customerProfileFields.some((field) => field.name === name);

  const text = (name: CustomerProfileField['name']) =>
    requirements[name]
      ? z.string().min(1, { message: t('text.mustBeFilled') })
      : z.string().optional();

  const phoneNumber = !isVisible('phoneNumber')
    ? z.string().optional()
    : requirements['phoneNumber']
      ? z
          .string()
          .min(1, { message: t('text.mustBeFilled') })
          .refine((val) => isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          })
      : z
          .string()
          .optional()
          .refine((val) => !val || isValidPhoneNumber(val), {
            message: t('text.invalidPhoneNumberFormat'),
          });

  return z.object({
    email: z.string().optional(),
    firstName: text('firstName'),
    lastName: text('lastName'),
    phoneNumber,
    birthDate: text('birthDate'),
    genderId: text('genderId'),
    countryId: text('countryId'),
    streetAddress: text('streetAddress'),
    city: text('city'),
    zipCode: text('zipCode'),
    dietaryRequirementIds: z.array(z.string()),
    seatPreferenceIds: z.array(z.string()),
  });
};

export type UpdateProfileValues = z.infer<
  ReturnType<typeof createUpdateProfileSchema>
>;
